import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import '../css/ProfilePage.css';
import logo from '../images/images.png';
import defaultLogo from '../images/default-logo.webp';
import { useCredits } from './CreditsContext';
import { useAuth } from '../contexts/AuthContext';
import PurchaseModal from './PurchaseModal';

const TABS = ['profile','company', 'ubo', 'authorized', 'documents', 'subscription', 'transactions'];

function ProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const subscriptionRef = useRef(null);
  const { transactions, screeningCredits, talentRolePass, shortlistPass, purchaseCredits, purchaseTalentRolePasses, purchaseShortlistPasses, logTransaction } = useCredits();
  const { currentUser } = useAuth();

  // User data state
  const [userData, setUserData] = useState({
    accountNumber: 'EX-00000',
    accountName: 'Loading...',
    jobTitle: 'Not Set',
    company: 'Loading...',
    isProfileComplete: false,
    isVerified: false,
    logoUrl: null
  });
  
  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationWaitModal, setShowVerificationWaitModal] = useState(false);
  const [verificationTimer, setVerificationTimer] = useState(10); // 10 seconds for presentation
  const [verificationStatus, setVerificationStatus] = useState(() => {
    // Try to load from localStorage first
    if (typeof window !== 'undefined' && currentUser?.uid) {
      try {
        const saved = localStorage.getItem(`exodia-verification-status-${currentUser.uid}`);
        if (saved && ['Unverified', 'In Progress', 'Verified'].includes(saved)) {
          return saved;
        }
      } catch (error) {
        console.error('Error loading verification status from localStorage:', error);
      }
    }
    return 'Unverified';
  });
  const [verificationTimerRef, setVerificationTimerRef] = useState(null); // Store timer reference
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Custom function to update verification status and save to localStorage
  const updateVerificationStatusLocal = (newStatus) => {
    console.log(`🔄 Updating verification status to: ${newStatus}`);
    setVerificationStatus(newStatus);
    
    // Save to localStorage for persistence across page refreshes
    if (typeof window !== 'undefined' && currentUser?.uid) {
      try {
        localStorage.setItem(`exodia-verification-status-${currentUser.uid}`, newStatus);
        console.log(`✅ Verification status saved to localStorage: ${newStatus}`);
      } catch (error) {
        console.error('Error saving verification status to localStorage:', error);
      }
    }
  };
  
  // Logo upload modal state
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [logoError, setLogoError] = useState('');
  
  // Document viewer modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  
  // Form state for verification modal - Complete form data structure
  const [formData, setFormData] = useState({
    // Agreement (Step 0)
    agreementAccepted: false,
    // Profile Information (Step 1)
    firstName: '',
    lastName: '',
    jobTitle: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    facebook: '',
    linkedin_profile: '',
    street: '',
    city: '',
    state_province: '',
    country: '',
    time_zone: '',
    zip_code: '',
    // Company Information (Step 2)
    company_name: '',
    registration_number: '',
    license_number: '',
    tax_registration: '',
    website: '',
    industry: '',
    size_type: '',
    operations_type: '',
    employee_count: '',
    annual_revenue: '',
    business_address: '',
    company_email: '',
    company_phone: '',
    secondary_company_phone: '',
    company_facebook: '',
    company_linkedin: '',
    // UBO Information (Step 3)
    ubo_first_name: '',
    ubo_last_name: '',
    ubo_nationality: '',
    ubo_birthdate: '',
    ubo_passport_id: '',
    ubo_ownership_share: '',
    // Authorized Signatory (Step 4)
    signatory_first_name: '',
    signatory_last_name: '',
    signatory_nationality: '',
    signatory_birthdate: '',
    signatory_passport_id: '',
    signatory_position: '',
  // Document Requirements (Step 5)
  documents: {},
  // Document Requirements Form Fields
  document_notes: '',
  verification_notes: '',
  special_requirements: '',
  additional_documents: '',
  compliance_notes: '',
  review_status: 'pending'
  });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [stepCompletion, setStepCompletion] = useState(() => {
    // Load stepCompletion from localStorage if available
    if (typeof window !== 'undefined' && currentUser) {
      try {
        const saved = localStorage.getItem(`exodia-step-completion-${currentUser.uid}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          console.log('📱 Loaded stepCompletion from localStorage:', parsed);
          return parsed;
        }
      } catch (error) {
        console.error('❌ Error loading stepCompletion from localStorage:', error);
      }
    }
    return {};
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});

  // Check verification status from backend (using boolean is_verified field)
  const checkVerificationStatus = useCallback(async () => {
    if (!currentUser) return false;

    try {
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      if (response.ok) {
        const result = await response.json();
        return result.data?.is_verified === true;
      }
    } catch (error) {
      console.log('⚠️ Error checking verification status:', error.message);
    }
    return false;
  }, [currentUser]);

  // Fetch user data from database with proper error handling
  const fetchUserData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');
      console.log('🔄 Fetching user data...');

      // Generate account number based on user ID
      const accountNumber = `EX-${currentUser.uid.slice(-5).toUpperCase()}`;

      // Fetch user data from Users table for auto-population
      let usersTableData = null;
      try {
        const usersResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
        if (usersResponse.ok) {
          const usersResult = await usersResponse.json();
          usersTableData = usersResult.data;
          console.log('✅ Users table data fetched:', usersTableData);
        } else {
          console.log('⚠️ Users table fetch failed with status:', usersResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Error fetching users table data:', error.message);
      }

      // Fetch profile information with error handling
      let profileData = null;
      try {
        const profileResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/profile-information?userId=${currentUser.uid}`);
        if (profileResponse.ok) {
          profileData = await profileResponse.json();
          console.log('✅ Profile data fetched successfully');
        } else if (profileResponse.status === 404) {
          console.log('⚠️ Profile information not found (404) - this is normal for new users');
        } else {
          console.log('⚠️ Profile fetch failed with status:', profileResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Error fetching profile information:', error.message);
      }

      // Fetch company information with error handling
      let companyData = null;
      try {
        const companyResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/company-information?userId=${currentUser.uid}`);
        if (companyResponse.ok) {
          companyData = await companyResponse.json();
          console.log('✅ Company data fetched successfully');
        } else if (companyResponse.status === 404) {
          console.log('⚠️ Company information not found (404) - this is normal for new users');
        } else {
          console.log('⚠️ Company fetch failed with status:', companyResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Error fetching company information:', error.message);
      }

      // Check verification status
      const isVerified = await checkVerificationStatus();
      
      // Get verification status from backend (using boolean is_verified field)
      let verificationStatus = 'Unverified';
      try {
        const statusResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
        if (statusResponse.ok) {
          const statusResult = await statusResponse.json();
          const isVerified = statusResult.data?.is_verified;
          
          // Check localStorage for "In Progress" status first
          const localStatus = localStorage.getItem(`exodia-verification-status-${currentUser.uid}`);
          if (localStatus === 'In Progress') {
            verificationStatus = 'In Progress';
          } else if (isVerified === true) {
            verificationStatus = 'Verified';
          } else {
            verificationStatus = 'Unverified';
          }
        }
      } catch (error) {
        console.log('⚠️ Error fetching verification status:', error.message);
      }

      // Process user data
      const processedUserData = {
        accountNumber: accountNumber,
        accountName: profileData?.data?.first_name && profileData?.data?.last_name 
          ? `${profileData.data.first_name} ${profileData.data.last_name}` 
          : currentUser.displayName || 'User',
        jobTitle: profileData?.data?.job_title || 'Not Set',
        company: companyData?.data?.company_name || 'Not Set',
        isProfileComplete: !!(profileData?.data?.first_name && profileData?.data?.last_name && profileData?.data?.job_title),
        isVerified: isVerified
      };

      setUserData(processedUserData);
      
      // Only update verification status if it's not currently "In Progress"
      // This prevents overriding the status during the timer countdown
      if (verificationStatus !== 'In Progress') {
        console.log('✅ Updating verification status from backend:', verificationStatus);
        updateVerificationStatusLocal(verificationStatus);
      } else {
        console.log('🔄 Keeping verification status as In Progress (timer active)');
      }
      
      console.log('✅ User data processed:', processedUserData);

      // Auto-populate form data with Users table data if no existing data
      if (usersTableData) {
        setFormData(prev => ({
          ...prev,
          // Profile Information auto-population from Users table
          firstName: prev.firstName || usersTableData.first_name || '',
          lastName: prev.lastName || usersTableData.last_name || '',
          primaryEmail: prev.primaryEmail || usersTableData.email || '',
          primaryPhone: prev.primaryPhone || usersTableData.phone_number || '',
          // Company Information auto-population from Users table
          company_name: prev.company_name || usersTableData.company_name || '',
          industry: prev.industry || usersTableData.industry || '',
          country: prev.country || usersTableData.country || ''
        }));
        console.log('✅ Form data auto-populated from Users table');
      }

      // Load user logo
      await loadUserLogo();

    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, checkVerificationStatus]);

  // Load all verification form data from database
  const loadAllFormData = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      console.log('🔄 Loading all saved form data...');
      
      // Fetch Users table data for fallback auto-population
      let usersTableData = null;
      try {
        const usersResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
        if (usersResponse.ok) {
          const usersResult = await usersResponse.json();
          usersTableData = usersResult.data;
          console.log('✅ Users table data fetched for form loading:', usersTableData);
        }
      } catch (error) {
        console.log('⚠️ Error fetching users table data for form loading:', error.message);
      }
      
      // Load Profile Information (Step 1)
      try {
        const profileResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/profile-information?userId=${currentUser.uid}`);
        if (profileResponse.ok) {
          const profileResult = await profileResponse.json();
          if (profileResult.status === 'success' && profileResult.data) {
            const profileData = profileResult.data;
            console.log('✅ Profile data loaded:', profileData);
            
            // Update form data with profile information (with Users table fallback)
            setFormData(prev => ({
              ...prev,
              firstName: profileData.first_name || usersTableData?.first_name || '',
              lastName: profileData.last_name || usersTableData?.last_name || '',
              jobTitle: profileData.job_title || '',
              primaryEmail: profileData.primary_email || usersTableData?.email || '',
              secondaryEmail: profileData.secondary_email || '',
              primaryPhone: profileData.primary_phone || usersTableData?.phone_number || '',
              secondaryPhone: profileData.secondary_phone || '',
              facebook: profileData.facebook || '',
              linkedin_profile: profileData.linkedin_profile || '',
              street: profileData.street || '',
              city: profileData.city || '',
              state_province: profileData.state_province || '',
              country: profileData.country || usersTableData?.country || '',
              time_zone: profileData.time_zone || '',
              zip_code: profileData.zip_code || ''
            }));
          }
        } else {
          // If no ProfileInformation found, populate from Users table
          if (usersTableData) {
            console.log('📝 No ProfileInformation found, populating from Users table');
            setFormData(prev => ({
              ...prev,
              firstName: prev.firstName || usersTableData.first_name || '',
              lastName: prev.lastName || usersTableData.last_name || '',
              primaryEmail: prev.primaryEmail || usersTableData.email || '',
              primaryPhone: prev.primaryPhone || usersTableData.phone_number || '',
              country: prev.country || usersTableData.country || ''
            }));
          }
        }
      } catch (error) {
        console.log('⚠️ Could not load profile data:', error);
        // Fallback to Users table data
        if (usersTableData) {
          console.log('📝 Error loading ProfileInformation, using Users table fallback');
          setFormData(prev => ({
            ...prev,
            firstName: prev.firstName || usersTableData.first_name || '',
            lastName: prev.lastName || usersTableData.last_name || '',
            primaryEmail: prev.primaryEmail || usersTableData.email || '',
            primaryPhone: prev.primaryPhone || usersTableData.phone_number || '',
            country: prev.country || usersTableData.country || ''
          }));
        }
      }
      
      // Load Company Information (Step 2)
      try {
        const companyResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/company-information?userId=${currentUser.uid}`);
        if (companyResponse.ok) {
          const companyResult = await companyResponse.json();
          if (companyResult.status === 'success' && companyResult.data) {
            const companyData = companyResult.data;
            console.log('✅ Company data loaded:', companyData);
            
            // Update form data with company information (with Users table fallback)
            setFormData(prev => ({
              ...prev,
              company_name: companyData.company_name || usersTableData?.company_name || '',
              registration_number: companyData.registration_number || '',
              license_number: companyData.license_number || '',
              tax_registration: companyData.tax_registration || '',
              website: companyData.website || '',
              industry: companyData.industry || usersTableData?.industry || '',
              size_type: companyData.size_type || '',
              operations_type: companyData.operations_type || '',
              employee_count: companyData.employee_count || '',
              annual_revenue: companyData.annual_revenue || '',
              business_address: companyData.business_address || '',
              company_email: companyData.company_email || '',
              company_phone: companyData.company_phone || '',
              secondary_company_phone: companyData.secondary_company_phone || '',
              company_facebook: companyData.company_facebook || '',
              company_linkedin: companyData.company_linkedin || ''
            }));
          }
        } else if (companyResponse.status === 404) {
          // If no CompanyInformation found, populate from Users table
          if (usersTableData) {
            console.log('📝 No CompanyInformation found, populating from Users table');
            setFormData(prev => ({
              ...prev,
              company_name: prev.company_name || usersTableData.company_name || '',
              industry: prev.industry || usersTableData.industry || ''
            }));
          }
        } else {
          console.log('⚠️ Company fetch failed with status:', companyResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Could not load company data:', error);
        // Fallback to Users table data
        if (usersTableData) {
          console.log('📝 Error loading CompanyInformation, using Users table fallback');
          setFormData(prev => ({
            ...prev,
            company_name: prev.company_name || usersTableData.company_name || '',
            industry: prev.industry || usersTableData.industry || ''
          }));
        }
      }
      
      // Load UBO Information (Step 3)
      try {
        const uboResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/ubo-information?userId=${currentUser.uid}`);
        if (uboResponse.ok) {
          const uboResult = await uboResponse.json();
          if (uboResult.status === 'success' && uboResult.data) {
            const uboData = uboResult.data;
            console.log('✅ UBO data loaded:', uboData);
            
            // Update form data with UBO information
            setFormData(prev => ({
              ...prev,
              ubo_first_name: uboData.ubo_first_name || '',
              ubo_last_name: uboData.ubo_last_name || '',
              ubo_nationality: uboData.ubo_nationality || '',
              ubo_birthdate: uboData.ubo_birthdate || '',
              ubo_passport_id: uboData.ubo_passport_id || '',
              ubo_ownership_share: uboData.ubo_ownership_share || ''
            }));
          }
        } else if (uboResponse.status === 404) {
          // Silently handle 404 for new users - no console log needed
        } else {
          console.log('⚠️ UBO fetch failed with status:', uboResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Could not load UBO data:', error);
      }
      
      // Load Authorized Signatory (Step 4)
      try {
        const signatoryResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/authorized-signatory?userId=${currentUser.uid}`);
        if (signatoryResponse.ok) {
          const signatoryResult = await signatoryResponse.json();
          if (signatoryResult.status === 'success' && signatoryResult.data) {
            const signatoryData = signatoryResult.data;
            console.log('✅ Signatory data loaded:', signatoryData);
            
            // Update form data with signatory information
            setFormData(prev => ({
              ...prev,
              signatory_first_name: signatoryData.signatory_first_name || '',
              signatory_last_name: signatoryData.signatory_last_name || '',
              signatory_nationality: signatoryData.signatory_nationality || '',
              signatory_birthdate: signatoryData.signatory_birthdate || '',
              signatory_passport_id: signatoryData.signatory_passport_id || '',
              signatory_position: signatoryData.signatory_position || ''
            }));
          }
        } else if (signatoryResponse.status === 404) {
          // Silently handle 404 for new users - no console log needed
        } else {
          console.log('⚠️ Signatory fetch failed with status:', signatoryResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Could not load signatory data:', error);
      }
      
      // Load Document Requirements (Step 5)
      try {
        const documentResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/document-requirements?userId=${currentUser.uid}`);
        if (documentResponse.ok) {
          const documentResult = await documentResponse.json();
          if (documentResult.status === 'success' && documentResult.data) {
            const documentsData = documentResult.data;
            
            // Convert array to object format for formData
            if (Array.isArray(documentsData)) {
              const documents = {};
              documentsData.forEach(doc => {
                console.log('📄 Processing document from database:', doc);
                if (doc.document_type && doc.file_name) {
                  documents[doc.document_type] = {
                    name: doc.file_name,
                    status: doc.status || 'uploaded',
                    uploadTime: doc.uploaded_at || new Date().toISOString(),
                    file_id: doc.file_id || '',
                    file_url: doc.file_url || '',
                    storage_path: doc.file_url ? doc.file_url.split('/').slice(-2).join('/') : ''
                  };
                  console.log('✅ Added document to formData:', doc.document_type, { 
                    name: doc.file_name, 
                    file_id: doc.file_id, 
                    file_url: doc.file_url ? 'Has data' : 'No data' 
                  });
                }
              });
              setFormData(prev => ({ ...prev, documents: { ...prev.documents, ...documents } }));
              console.log('✅ Documents restored from database');
            }
          }
        } else if (documentResponse.status === 404) {
          // Silently handle 404 for new users - no console log needed
        } else {
          console.log('⚠️ Document fetch failed with status:', documentResponse.status);
        }
      } catch (error) {
        console.log('⚠️ Could not load documents:', error);
      }
      
      console.log('✅ All form data loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading form data:', error);
    }
  }, [currentUser]);

  // Fetch data when component mounts
  useEffect(() => {
    if (currentUser) {
      fetchUserData();
      loadAllFormData(); // Load all saved form data
    }
  }, [currentUser, fetchUserData, loadAllFormData]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (verificationTimerRef) {
        clearInterval(verificationTimerRef);
      }
    };
  }, [verificationTimerRef]);

  // Clear verification status from localStorage when user logs out
  useEffect(() => {
    if (!currentUser?.uid) {
      // User logged out, clear localStorage and reset status
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(`exodia-verification-status-${currentUser?.uid || 'unknown'}`);
          console.log('🧹 Cleared verification status from localStorage on logout');
        } catch (error) {
          console.error('Error clearing verification status from localStorage:', error);
        }
      }
      // Reset verification status to Unverified when user logs out
      setVerificationStatus('Unverified');
    }
  }, [currentUser?.uid]);

  // Derive tab from URL query or navigation state
  const deriveTabFromLocation = (loc) => {
    const params = new URLSearchParams(loc.search);
    const fromQuery = params.get('tab');
    const fromState = loc.state?.openTab;
    const candidate = (fromQuery || fromState || '').toLowerCase();
    return TABS.includes(candidate) ? candidate : 'profile';
  };

  const [activeTab, setActiveTab] = useState(() => deriveTabFromLocation(location));
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  
  // Purchase modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseType, setPurchaseType] = useState('credits'); // 'credits', 'talentRole', or 'shortlistPass'
  

  // Keep component in sync if user navigates again to /profile?tab=...
  useEffect(() => {
    const target = deriveTabFromLocation(location);
    setActiveTab(target);
  }, [location]);

  // Smooth-scroll into the subscription section when that tab is active
  useEffect(() => {
    if (activeTab === 'subscription' && subscriptionRef.current) {
      requestAnimationFrame(() => {
        subscriptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [activeTab]);

  // Single helper to switch tab and reflect it in the URL
  const switchTab = (tab) => {
    setActiveTab(tab);
    navigate(
      { pathname: location.pathname, search: `?tab=${tab}` },
      { replace: false, state: { openTab: tab } }
    );
    
    // Load form data when switching to company tab to ensure data is available
    if (tab === 'company') {
      loadAllFormData();
    }
  };

  // Load verification data and determine current step
  const loadVerificationData = useCallback(async () => {
    if (!currentUser) return;

    try {
      // First, try to load stepCompletion from localStorage
      if (typeof window !== 'undefined') {
        try {
          const saved = localStorage.getItem(`exodia-step-completion-${currentUser.uid}`);
          if (saved) {
            const parsed = JSON.parse(saved);
            console.log('📱 Loaded stepCompletion from localStorage:', parsed);
            setStepCompletion(parsed);
          }
        } catch (error) {
          console.error('❌ Error loading stepCompletion from localStorage:', error);
        }
      }
      // Check if agreement was previously accepted (this would be stored in localStorage or backend)
      // For now, we'll assume if any other data exists, agreement was accepted
      let agreementAccepted = false;
      
      // Load profile information
      const profileResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/profile-information?userId=${currentUser.uid}`);
      if (profileResponse.ok) {
        const profileResult = await profileResponse.json();
        if (profileResult.status === 'success' && profileResult.data) {
          const profileData = profileResult.data;
          setFormData(prev => ({
            ...prev,
            firstName: profileData.first_name || '',
            lastName: profileData.last_name || '',
            jobTitle: profileData.job_title || '',
            primaryEmail: profileData.primary_email || '',
            secondaryEmail: profileData.secondary_email || '',
            primaryPhone: profileData.primary_phone || '',
            secondaryPhone: profileData.secondary_phone || '',
            facebook: profileData.facebook || '',
            linkedin_profile: profileData.linkedin_profile || '',
            street: profileData.street || '',
            city: profileData.city || '',
            state_province: profileData.state_province || '',
            country: profileData.country || '',
            time_zone: profileData.time_zone || '',
            zip_code: profileData.zip_code || ''
          }));
          setStepCompletion(prev => ({ ...prev, 1: true }));
        }
      }

      // Load company information
      const companyResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/company-information?userId=${currentUser.uid}`);
      if (companyResponse.ok) {
        const companyResult = await companyResponse.json();
        if (companyResult.status === 'success' && companyResult.data) {
          const companyData = companyResult.data;
          setFormData(prev => ({
            ...prev,
            company_name: companyData.company_name || '',
            registration_number: companyData.registration_number || '',
            license_number: companyData.license_number || '',
            tax_registration: companyData.tax_registration || '',
            website: companyData.website || '',
            industry: companyData.industry || '',
            size_type: companyData.size_type || '',
            operations_type: companyData.operations_type || '',
            employee_count: companyData.employee_count || '',
            annual_revenue: companyData.annual_revenue || '',
            business_address: companyData.business_address || '',
            company_email: companyData.company_email || '',
            company_phone: companyData.company_phone || '',
            secondary_company_phone: companyData.secondary_company_phone || '',
            company_facebook: companyData.company_facebook || '',
            company_linkedin: companyData.company_linkedin || ''
          }));
          setStepCompletion(prev => ({ ...prev, 2: true }));
        }
      }

      // Load UBO information
      const uboResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/ubo-information?userId=${currentUser.uid}`);
      if (uboResponse.ok) {
        const uboResult = await uboResponse.json();
        if (uboResult.status === 'success' && uboResult.data) {
          const uboData = uboResult.data;
          setFormData(prev => ({
            ...prev,
            ubo_first_name: uboData.ubo_first_name || '',
            ubo_last_name: uboData.ubo_last_name || '',
            ubo_nationality: uboData.ubo_nationality || '',
            ubo_birthdate: uboData.ubo_birthdate || '',
            ubo_passport_id: uboData.ubo_passport_id || '',
            ubo_ownership_share: uboData.ubo_ownership_share || ''
          }));
          setStepCompletion(prev => ({ ...prev, 3: true }));
        }
      }

      // Load authorized signatory information
      const signatoryResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/authorized-signatory?userId=${currentUser.uid}`);
      if (signatoryResponse.ok) {
        const signatoryResult = await signatoryResponse.json();
        if (signatoryResult.status === 'success' && signatoryResult.data) {
          const signatoryData = signatoryResult.data;
          setFormData(prev => ({
            ...prev,
            signatory_first_name: signatoryData.signatory_first_name || '',
            signatory_last_name: signatoryData.signatory_last_name || '',
            signatory_nationality: signatoryData.signatory_nationality || '',
            signatory_birthdate: signatoryData.signatory_birthdate || '',
            signatory_passport_id: signatoryData.signatory_passport_id || '',
            signatory_position: signatoryData.signatory_position || ''
          }));
          setStepCompletion(prev => ({ ...prev, 4: true }));
        }
      }

      // Load documents information
      const documentsResponse = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/documents?userId=${currentUser.uid}`);
      if (documentsResponse.ok) {
        const documentsResult = await documentsResponse.json();
        if (documentsResult.status === 'success' && documentsResult.data) {
          const documentsData = documentsResult.data;
          setFormData(prev => ({
            ...prev,
            documents: documentsData.documents || {},
            document_notes: documentsData.document_notes || '',
            verification_notes: documentsData.verification_notes || '',
            special_requirements: documentsData.special_requirements || '',
            additional_documents: documentsData.additional_documents || '',
            compliance_notes: documentsData.compliance_notes || '',
            review_status: documentsData.review_status || 'pending'
          }));
          setStepCompletion(prev => ({ ...prev, 5: true }));
        }
      }

      // If any data was loaded, mark agreement as accepted
      // We'll check this after all the data loading is complete
      setTimeout(() => {
        setStepCompletion(prev => {
          const hasAnyData = Object.values(prev).some(Boolean);
          if (hasAnyData) {
            setFormData(formPrev => ({ ...formPrev, agreementAccepted: true }));
            return { ...prev, 0: true };
          }
          return prev;
        });
      }, 100);

    } catch (error) {
      console.log('⚠️ Error loading verification data:', error);
    }
  }, [currentUser]);

  // Persist stepCompletion to localStorage whenever it changes
  useEffect(() => {
    if (currentUser && typeof window !== 'undefined') {
      try {
        localStorage.setItem(`exodia-step-completion-${currentUser.uid}`, JSON.stringify(stepCompletion));
        console.log('💾 Saved stepCompletion to localStorage:', stepCompletion);
      } catch (error) {
        console.error('❌ Error saving stepCompletion to localStorage:', error);
      }
    }
  }, [stepCompletion, currentUser]);

  // Determine the current step based on completed steps
  const determineCurrentStep = useCallback(() => {
    const completedSteps = Object.values(stepCompletion).filter(Boolean).length;
    
    // If no steps are completed, start from Agreement (step 0)
    if (completedSteps === 0) {
      return 0;
    }
    
    // If all steps are completed, start from Documents (step 5) for review
    if (completedSteps === 6) {
      return 5;
    }
    
    // Otherwise, start from the first incomplete step
    for (let i = 0; i < 6; i++) {
      if (!stepCompletion[i]) {
        return i;
      }
    }
    
    return 0; // Default fallback
  }, [stepCompletion]);

  // Handle edit tab function
  const handleEditTab = (tabName) => {
    // Set the current step based on the tab
    let stepNumber;
    switch (tabName) {
      case 'profile':
        stepNumber = 1;
        break;
      case 'company':
        stepNumber = 2;
        break;
      case 'ubo':
        stepNumber = 3;
        break;
      case 'authorized':
        stepNumber = 4;
        break;
      case 'documents':
        stepNumber = 5;
        break;
      default:
        stepNumber = 1;
    }
    
    // Set the current step and open verification modal
    setCurrentStep(stepNumber);
    setShowVerificationModal(true);
  };

  // Handle opening verification modal from Verify Account button
  const openVerificationModal = async () => {
    // Load existing verification data
    await loadVerificationData();
    
    // Determine the current step based on completed steps
    const currentStepNumber = determineCurrentStep();
    setCurrentStep(currentStepNumber);
    
    // Open the modal
    setShowVerificationModal(true);
  };

  // Form handling functions for verification modal
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Step navigation functions
  const nextStep = async () => {
    // For step 0 (agreement), just move to next step
    if (currentStep === 0) {
      if (formData.agreementAccepted) {
        setCurrentStep(1);
        setStepCompletion(prev => ({ ...prev, 0: true }));
      } else {
        window.alert('Please accept the agreement to continue.');
        return;
      }
    } else {
      // For other steps, save data first
      const saved = await saveStepData(currentStep);
      
      if (saved) {
        if (currentStep < 5) {
          setCurrentStep(currentStep + 1);
        }
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Form validation for each step
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return formData.agreementAccepted;
      case 1:
        return formData.firstName && formData.lastName && formData.jobTitle && 
               formData.primaryEmail && formData.primaryPhone && 
               formData.street && formData.city && formData.state_province && 
               formData.country && formData.time_zone && formData.zip_code;
      case 2:
        return formData.company_name && formData.registration_number && 
               formData.tax_registration && formData.website && 
               formData.industry && formData.size_type && formData.operations_type && 
               formData.employee_count && formData.business_address && 
               formData.company_email && formData.company_phone;
      case 3:
        return formData.ubo_first_name && formData.ubo_last_name && 
               formData.ubo_nationality && formData.ubo_birthdate && 
               formData.ubo_passport_id && formData.ubo_ownership_share;
      case 4:
        return formData.signatory_first_name && formData.signatory_last_name && 
               formData.signatory_nationality && formData.signatory_birthdate && 
               formData.signatory_passport_id && formData.signatory_position;
      case 5:
        // For presentation: documents are optional, always return true
        return true;
      default:
        return true;
    }
  };

  // Comprehensive validation for all steps
  const validateAllSteps = () => {
    const stepValidations = {
      0: formData.agreementAccepted,
      1: formData.firstName && formData.lastName && formData.jobTitle && 
         formData.primaryEmail && formData.primaryPhone && 
         formData.street && formData.city && formData.state_province && 
         formData.country && formData.time_zone && formData.zip_code,
      2: formData.company_name && formData.registration_number && 
         formData.tax_registration && formData.website && 
         formData.industry && formData.size_type && formData.operations_type && 
         formData.employee_count && formData.business_address && 
         formData.company_email && formData.company_phone,
      3: formData.ubo_first_name && formData.ubo_last_name && 
         formData.ubo_nationality && formData.ubo_birthdate && 
         formData.ubo_passport_id && formData.ubo_ownership_share,
      4: formData.signatory_first_name && formData.signatory_last_name && 
         formData.signatory_nationality && formData.signatory_birthdate && 
         formData.signatory_passport_id && formData.signatory_position,
      5: true // For presentation: documents are optional
    };

    // Check which steps are incomplete
    const incompleteSteps = [];
    for (let step = 0; step <= 5; step++) {
      if (!stepValidations[step]) {
        incompleteSteps.push(step);
      }
    }

    return {
      isValid: incompleteSteps.length === 0,
      incompleteSteps: incompleteSteps
    };
  };

  // Save step data to database (CREATE or UPDATE) - Unified approach
  const saveStepData = async (stepNumber) => {
    if (!currentUser) return;
    
    try {
      console.log(`💾 Saving step ${stepNumber} data...`);
      
      let endpoint = '';
      let formDataToSave = {};
      
      // Determine endpoint and data based on step
      switch (stepNumber) {
        case 1: // Profile Information
          endpoint = 'profile-information';
          formDataToSave = {
            user_id: currentUser.uid,
            first_name: formData.firstName || '',
            last_name: formData.lastName || '',
            job_title: formData.jobTitle || '',
            primary_email: formData.primaryEmail || '',
            secondary_email: formData.secondaryEmail || '',
            primary_phone: formData.primaryPhone || '',
            secondary_phone: formData.secondaryPhone || '',
            facebook: formData.facebook || '',
            linkedin_profile: formData.linkedin_profile || '',
            street: formData.street || '',
            city: formData.city || '',
            state_province: formData.state_province || '',
            country: formData.country || '',
            time_zone: formData.time_zone || '',
            zip_code: formData.zip_code || ''
          };
          break;
          
        case 2: // Company Information
          endpoint = 'company-information';
          formDataToSave = {
            user_id: currentUser.uid,
            company_name: formData.company_name || '',
            registration_number: formData.registration_number || '',
            license_number: formData.license_number || '',
            tax_registration: formData.tax_registration || '',
            website: formData.website || '',
            industry: formData.industry || '',
            size_type: formData.size_type || '',
            operations_type: formData.operations_type || '',
            employee_count: formData.employee_count && formData.employee_count.toString().trim() !== '' ? parseInt(formData.employee_count) || 0 : 0,
            annual_revenue: formData.annual_revenue || '',
            business_address: formData.business_address || '',
            company_email: formData.company_email || '',
            company_phone: formData.company_phone || '',
            secondary_company_phone: formData.secondary_company_phone || '',
            company_facebook: formData.company_facebook || '',
            company_linkedin: formData.company_linkedin || ''
          };
          console.log('🏢 Company data to save:', formDataToSave);
          break;
          
        case 3: // UBO Information
          endpoint = 'ubo-information';
          formDataToSave = {
            user_id: currentUser.uid,
            ubo_first_name: formData.ubo_first_name || '',
            ubo_last_name: formData.ubo_last_name || '',
            ubo_nationality: formData.ubo_nationality || '',
            ubo_birthdate: formData.ubo_birthdate && formData.ubo_birthdate.toString().trim() !== '' ? formData.ubo_birthdate : null,
            ubo_passport_id: formData.ubo_passport_id || '',
            ubo_ownership_share: formData.ubo_ownership_share && formData.ubo_ownership_share.toString().trim() !== '' ? parseInt(formData.ubo_ownership_share) || 0 : 0
          };
          break;
          
        case 4: // Authorized Signatory
          endpoint = 'authorized-signatory';
          formDataToSave = {
            user_id: currentUser.uid,
            signatory_first_name: formData.signatory_first_name || '',
            signatory_last_name: formData.signatory_last_name || '',
            signatory_nationality: formData.signatory_nationality || '',
            signatory_birthdate: formData.signatory_birthdate && formData.signatory_birthdate.toString().trim() !== '' ? formData.signatory_birthdate : null,
            signatory_passport_id: formData.signatory_passport_id || '',
            signatory_position: formData.signatory_position || ''
          };
          break;
          
        case 5: // Document Requirements - Skip static creation
          // Documents are now handled by individual upload functions
          // Return true to indicate step is "saved" (documents are handled separately)
          console.log('✅ Step 5 (Documents) - handled by individual upload functions');
          return true;
          
        default:
          throw new Error('Invalid step number');
      }
      
      // Save to Catalyst DataStore (this will handle CREATE or UPDATE automatically)
      console.log(`🚀 Sending ${endpoint} request:`, {
        url: `https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/${endpoint}`,
        data: formDataToSave
      });
      
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataToSave)
      });
      
      console.log(`📡 ${endpoint} response status:`, response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Step ${stepNumber} data saved successfully:`, result);
        
        // Mark step as completed
        setStepCompletion(prev => ({ ...prev, [stepNumber]: true }));
        
        // Show success message
        setError('');
        console.log(`✅ Step ${stepNumber} data saved successfully!`);
        
        return true;
      } else {
        const errorResult = await response.json();
        console.error(`❌ Failed to save step ${stepNumber} data:`, errorResult);
        setError(`Failed to save step ${stepNumber} data: ${errorResult.message}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error saving step ${stepNumber} data:`, error);
      setError(`Error saving step ${stepNumber} data: ${error.message}`);
      return false;
    }
  };

  const saveDraft = async () => {
    try {
      setLoading(true);
      
      // Validate required fields before sending
      if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
        window.alert('Please fill in First Name and Last Name before saving.');
        setLoading(false);
        return;
      }
      
      // Save current step data
      const saved = await saveStepData(currentStep);
      
      if (saved) {
        window.alert('Draft saved successfully!');
      }
      
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      setError(`Error saving draft: ${error.message}`);
      window.alert(`Error saving draft: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveAndContinue = async () => {
    try {
      setLoading(true);
      
      // Validate current step
      if (!validateCurrentStep()) {
        window.alert('Please fill in all required fields before continuing.');
        setLoading(false);
        return;
      }

      // Save current step data
      const saved = await saveStepData(currentStep);
      
      if (saved) {
        // Mark current step as completed
        setStepCompletion(prev => ({ ...prev, [currentStep]: true }));
        // Move to next step
        nextStep();
      }
      
    } catch (error) {
      console.error('❌ Error saving step:', error);
      setError(`Error saving step: ${error.message}`);
      window.alert(`Error saving step: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save and continue later function - allows saving partial data and navigation
  const saveAndContinueLater = async () => {
    try {
      setLoading(true);
      
      // Save current step data without validation - allow partial data
      const saved = await saveStepData(currentStep);
      
      if (saved) {
        // Move to next step if not on the last step
        if (currentStep < 5) {
          setCurrentStep(currentStep + 1);
        } else {
          // If on last step, close the modal
          setShowVerificationModal(false);
        }
      }
      
    } catch (error) {
      console.error('❌ Error saving progress:', error);
      setError(`Error saving progress: ${error.message}`);
      window.alert(`Error saving progress: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Complete verification - upload files and set status to Verified
  const completeVerification = async () => {
    // Save current step data
    const saved = await saveStepData(currentStep);
    
    if (saved) {
      // Mark all steps as completed
      setStepCompletion({
        1: true,
        2: true,
        3: true,
        4: true,
        5: true
      });
      
      // Set account status to "Verified" when user completes verification
      setUserData(prev => ({
        ...prev,
        isVerified: true
      }));
      
      // Close the modal
      setShowVerificationModal(false);
    }
  };

  const handleVerification = async () => {
    try {
      setLoading(true);
      
      // Validate all steps comprehensively
      const validation = validateAllSteps();
      
      if (!validation.isValid) {
        const stepNames = ['Agreement', 'Personal Information', 'Company Information', 'UBO Information', 'Signatory Information', 'Documents'];
        const incompleteStepNames = validation.incompleteSteps.map(step => stepNames[step]).join(', ');
        window.alert(`Please complete all required fields in the following steps before submitting: ${incompleteStepNames}. Documents are optional for this demo. You can use "Save Progress" to save your current progress.`);
        setLoading(false);
        return;
      }
      
      // Save current step data first
      console.log('💾 Saving current step data before verification...');
      const saved = await saveStepData(currentStep);
      console.log('💾 Save result:', saved);
      
      if (saved) {
        // Call the account verification endpoint
        console.log('🚀 Calling account verification endpoint...');
        const verificationResponse = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/account-verification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: currentUser.uid,
            email: formData.primaryEmail || currentUser.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            job_title: formData.jobTitle,
            primary_email: formData.primaryEmail || currentUser.email,
            secondary_email: formData.secondaryEmail,
            facebook: formData.facebook,
            linkedin_profile: formData.linkedin_profile,
            street: formData.street,
            city: formData.city,
            state_province: formData.state_province,
            time_zone: formData.time_zone,
            zip_code: formData.zip_code
          })
        });
        
        console.log('📡 Verification response status:', verificationResponse.status);
        
        if (verificationResponse.ok) {
          const verificationResult = await verificationResponse.json();
          console.log('✅ Verification submitted successfully:', verificationResult);
          
          // Set status to "In Progress" instead of "Verified"
          await updateVerificationStatus('In Progress');
          
          // Mark all steps as completed
          setStepCompletion({
            0: true,
            1: true,
            2: true,
            3: true,
            4: true,
            5: true
          });
          
          // Update verification status to "In Progress"
          console.log('🔄 Setting verification status to In Progress');
          updateVerificationStatusLocal('In Progress');
          console.log('✅ Local state updated to In Progress');
          
          // Close verification modal and show wait modal
          setShowVerificationModal(false);
          setShowVerificationWaitModal(true);
          
          // Start the timer
          const timerRef = startVerificationTimer();
          setVerificationTimerRef(timerRef);
        } else {
          const errorResult = await verificationResponse.json();
          console.error('❌ Verification failed:', errorResult);
          throw new Error(errorResult.message || 'Failed to submit verification');
        }
      }
      
    } catch (error) {
      console.error('❌ Error submitting verification:', error);
      setError(`Error submitting verification: ${error.message}`);
      window.alert(`Error submitting verification: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update verification status using the proper endpoint (working with boolean is_verified field)
  const updateVerificationStatus = async (status) => {
    try {
      console.log(`🔄 Updating verification status to: ${status}`);
      
      // Convert status to boolean for backend
      const isVerified = status === 'Verified';
      
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/update-account-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser.uid,
          status: status,
          is_verified: isVerified
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Verification status updated successfully:', result);
      } else {
        console.error('❌ Failed to update verification status');
      }
    } catch (error) {
      console.error('❌ Error updating verification status:', error);
    }
  };

  // Start verification timer
  const startVerificationTimer = () => {
    setVerificationTimer(10); // 10 seconds for presentation
    
    const timer = setInterval(() => {
      setVerificationTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          completeVerificationAfterTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Store timer reference so it can be cleared if needed
    return timer;
  };

  // Complete verification after timer expires
  const completeVerificationAfterTimer = async () => {
    try {
      // Clear timer reference
      setVerificationTimerRef(null);
      
      // Update status to "Verified"
      await updateVerificationStatus('Verified');
      
      // Update local state
      console.log('🔄 Setting verification status to Verified');
      updateVerificationStatusLocal('Verified');
      setUserData(prev => ({
        ...prev,
        isVerified: true
      }));
      console.log('✅ Local state updated to Verified');
      
      // Close wait modal - verification complete
      setShowVerificationWaitModal(false);
      
      // Don't refresh user data here as it might override the local state
      // The status is already updated locally and in the backend
      
    } catch (error) {
      console.error('❌ Error completing verification:', error);
    }
  };

  // Update verification status using the proper endpoint
  const updateVerificationStatusInAllTables = async () => {
    try {
      console.log('🔄 Updating verification status using update-account-status endpoint...');
      
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/update-account-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser.uid,
          status: 'Verified'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Verification status updated successfully:', result);
      } else {
        const errorResult = await response.json();
        console.error('❌ Failed to update verification status:', errorResult);
        throw new Error(errorResult.message || 'Failed to update verification status');
      }
      
    } catch (error) {
      console.error('❌ Error updating verification status:', error);
      throw error;
    }
  };


  // Document upload functions
  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          window.alert(`File too large! Maximum size is 10MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
          return;
        }

        // Validate file type - only PDF files allowed
        const allowedTypes = ['application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          window.alert('Invalid file type! Please upload PDF files only.');
          return;
        }

        // Upload to Firebase and save metadata
        await handleDocumentUpload(file, fieldName);
      } catch (error) {
        console.error('Error selecting file:', error);
        window.alert('Error selecting file. Please try again.');
        // Reset the file input
        event.target.value = '';
      }
    }
  };

  // Upload document to Firebase through backend
  const uploadDocumentToFirebase = async (file, documentType) => {
    if (!currentUser) throw new Error('User not authenticated');

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 10MB.');
    }

    // Convert file to base64 for backend upload
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Upload through the working /upload-document endpoint
    const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/upload-document', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user_id: currentUser.uid,
        document_type: documentType,
        file_name: file.name,
        file_data: base64Data,
        file_type: file.type
      })
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    const result = await response.json();
    return {
      fileName: result.data.file_name || file.name,
      storagePath: result.data.file_id,
      downloadURL: result.data.file_url,
      fileId: result.data.file_id
    };
  };

  // Handle document upload to backend - Direct to Zoho Catalyst
  const handleDocumentUpload = async (file, documentType) => {
    if (!currentUser) {
      window.alert('Please log in to upload documents');
      return;
    }
    setUploadingDocument(true);
    try {
      console.log(`Uploading ${documentType}...`);
      
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // Upload directly to DocumentRequirements table
      // Create unique file name to avoid folder conflicts
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseFileName = file.name.replace(/\.[^/.]+$/, "");
      const uniqueFileName = `${baseFileName}_${currentUser.uid.slice(-8)}_${timestamp}.${fileExtension}`;
      
      const requestBody = {
        user_id: currentUser.uid,
        document_type: documentType,
        file_name: uniqueFileName,
        file_type: file.type,
        file_data: base64
      };
      
      console.log('Uploading document with request body:', {
        user_id: requestBody.user_id,
        document_type: requestBody.document_type,
        original_file_name: file.name,
        unique_file_name: requestBody.file_name,
        file_type: requestBody.file_type,
        file_data_length: requestBody.file_data ? requestBody.file_data.length : 0
      });
      
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/upload-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload response:', result);
        
        // Update form data with uploaded document
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            [documentType]: {
              name: file.name, // Keep original name for display
              uniqueName: uniqueFileName, // Store unique name for backend
              status: 'uploaded',
              uploadTime: new Date().toISOString(),
              file_id: result.file_id,
              file_url: result.file_url,
              document_type: documentType
            }
          }
        }));
        console.log(`✅ ${documentType} uploaded and saved successfully`);
        window.alert(`✅ ${documentType.replace(/_/g, ' ')} uploaded successfully!`);
      } else {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error);
      window.alert(`Failed to upload ${documentType.replace(/_/g, ' ')}. Please try again.`);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Remove uploaded document
  const removeDocument = (fieldName) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [fieldName]: null
      }
    }));
    console.log(`Document ${fieldName} removed`);
    // Reset the file input
    const fileInput = document.querySelector(`input[onchange*="${fieldName}"]`);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // View document function
  const viewDocument = (documentType) => {
    const document = formData.documents?.[documentType];
    if (document && document.file_url) {
      setCurrentDocument({
        type: documentType,
        name: document.name,
        url: document.file_url,
        uploadTime: document.uploadTime
      });
      setShowDocumentModal(true);
    } else {
      window.alert('Document not found or not uploaded yet.');
    }
  };

  // Close document modal
  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setCurrentDocument(null);
  };

  // Calculate experience based on verification quest progress
  const questRewards = {
    0: 20,  // Agreement
    1: 40,  // Profile
    2: 60,  // Company
    3: 80,  // UBO
    4: 100, // Signatory
    5: 120  // Documents
  };
  
  const totalQuestXP = 420; // Total possible XP from all quests
  let earnedXP = 0;
  
  // Calculate earned XP based on completed steps
  Object.keys(questRewards).forEach(step => {
    if (stepCompletion[parseInt(step)]) {
      earnedXP += questRewards[step];
    }
  });
  
  // Calculate total experience - if verified, give full XP
  const totalExperience = verificationStatus === 'Verified' ? totalQuestXP : earnedXP;
  const nextLevelExperience = totalQuestXP; // Target is total quest XP

  const profileData = {
    experience: totalExperience,
    nextLevel: nextLevelExperience,
    currentPlan: 'Basic'
  };

  const subscriptionPlans = [
    {
      name: 'Free',
      price: '$0',
      current: profileData.currentPlan === 'Free',
      benefits: ['3 credits', '1 talent roles', '1 access to talent roles', 'Talent sourcing and pooling'],
      features: ['Basic support', 'Standard features']
    },
    {
      name: 'Basic',
      price: '$339.99',
      current: profileData.currentPlan === 'Basic',
      benefits: ['10 credits', '3 talent roles', '3 access to talent roles', 'Talent sourcing and pooling'],
      features: ['Priority support', 'Advanced features', 'Analytics dashboard']
    },
    {
      name: 'Premium',
      price: '$499.99',
      current: profileData.currentPlan === 'Premium',
      benefits: ['20 credits', '5 talent roles', '5 access to talent roles', 'Talent sourcing and pooling'],
      features: ['Premium support', 'All features', 'Advanced analytics', 'Custom integrations']
    },
    {
      name: 'Prestige',
      price: 'Contact Sales',
      current: profileData.currentPlan === 'Prestige',
      benefits: ['Unlimited credits', 'Unlimited talent roles', 'Unlimited access to talent roles', 'Talent sourcing and pooling'],
      features: ['Dedicated support', 'Custom solutions', 'White-label options', 'API access']
    }
  ];

  const addOns = [
    { name: 'Credit', price: '$99.99', description: 'Additional credits for your account', icon: '🎫' },
    { name: 'Talent Role', price: '$29.99', description: 'Additional talent role access', icon: '👥' },
    { name: 'Shortlist Pass', price: '$9.99', description: 'Additional shortlist passes for talent selection', icon: '📋' }
  ];

  const progressPercentage = Math.min((profileData.experience / profileData.nextLevel) * 100, 100);

  const handlePlanClick = (plan) => {
    if (activeTab !== 'subscription') switchTab('subscription');
    setSelectedPlan(plan);
    setShowPlanModal(true);
  };

  const closePlanModal = () => {
    setShowPlanModal(false);
    setSelectedPlan(null);
  };

  const handleUpgrade = (planName) => {
    console.log(`Upgrading to ${planName} plan`);
    closePlanModal();
  };

  // Purchase modal handlers
  const handleAddClick = (type) => {
    setPurchaseType(type);
    setShowPurchaseModal(true);
  };

  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    setPurchaseType('credits');
  };


  const handlePurchaseConfirm = async (quantity, totalPrice) => {
    try {
      if (purchaseType === 'credits') {
        await purchaseCredits(quantity, totalPrice);
      } else if (purchaseType === 'talentRole') {
        await purchaseTalentRolePasses(quantity, totalPrice);
      } else if (purchaseType === 'shortlistPass') {
        await purchaseShortlistPasses(quantity, totalPrice);
      }
      const itemName = purchaseType === 'credits' ? 'credits' : purchaseType === 'talentRole' ? 'talent role passes' : 'shortlist passes';
      console.log(`Successfully purchased ${quantity} ${itemName} for $${totalPrice.toFixed(2)}`);
    } catch (error) {
      console.error('Purchase failed:', error);
      // You could add a toast notification here
    }
  };


  // Logo upload functions
  const handleLogoClick = () => {
    setShowLogoModal(true);
    setLogoError('');
  };

  // Enhanced file validation
  const validateLogoFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (file.size > maxSize) {
      setLogoError('File size must be less than 5MB');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Only JPG, PNG, GIF, and WebP images are allowed');
      return false;
    }
    
    // Check file signature for security
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const uint8Array = new Uint8Array(arrayBuffer);
      const signature = Array.from(uint8Array.slice(0, 4))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('').toUpperCase();
      
      const validSignatures = ['FFD8FF', '89504E47', '47494638', '52494646'];
      if (!validSignatures.some(sig => signature.startsWith(sig))) {
        setLogoError('Invalid file format detected');
        return false;
      }
    };
    reader.readAsArrayBuffer(file.slice(0, 4));
    
    setLogoError('');
    return true;
  };

  // Image compression function
  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, file.type, quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // File to base64 conversion
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };



  // Remove logo from Catalyst database
  const removeLogoFromDatabase = async () => {
    try {
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/update-user-logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser?.uid,
          logo_url: null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove logo from database');
      }

      const result = await response.json();
      console.log('Logo removed from database:', result);
    } catch (error) {
      console.error('Error removing logo from database:', error);
      throw error;
    }
  };

  // Load user logo from database with caching
  const loadUserLogo = async () => {
    if (!currentUser?.uid) return;
    
    // Check cache first
    const cacheKey = `logo_${currentUser.uid}`;
    const cachedLogo = localStorage.getItem(cacheKey);
    const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
    const cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
    
    if (cachedLogo && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < cacheExpiry) {
      setUserData(prev => ({ ...prev, logoUrl: cachedLogo }));
      return;
    }
    
    try {
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data?.logo_url) {
          const logoUrl = result.data.logo_url;
          setUserData(prev => ({ ...prev, logoUrl }));
          
          // Cache the logo URL
          localStorage.setItem(cacheKey, logoUrl);
          localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
        } else {
          // No custom logo found, use default logo
          setUserData(prev => ({ ...prev, logoUrl: null }));
        }
      }
    } catch (error) {
      console.error('Error loading user logo:', error);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoError('');
      
      // Validate file using enhanced validation
      if (!validateLogoFile(file)) {
        return;
      }
      
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !currentUser) return;
    
    setUploadingLogo(true);
    setUploadProgress(0);
    setLogoError('');
    
    try {
      // Step 1: Compress image (25% progress)
      setUploadProgress(25);
      const compressedFile = await compressImage(logoFile);
      console.log(`Image compressed: ${logoFile.size} -> ${compressedFile.size} bytes`);
      
      // Step 2: Convert to base64 (50% progress)
      setUploadProgress(50);
      const base64 = await fileToBase64(compressedFile);
      
      console.log('Uploading logo:', {
        fileName: logoFile.name,
        originalSize: logoFile.size,
        compressedSize: compressedFile.size,
        fileType: logoFile.type,
        userId: currentUser.uid,
        base64Length: base64.length
      });
      
      // Step 3: Upload file as base64 JSON (75% progress)
      setUploadProgress(75);
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/upload-logo-base64', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser.uid,
          file_name: logoFile.name,
          file_type: logoFile.type,
          file_data: base64
        })
      });
      
      console.log('Upload response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Failed to upload logo: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Upload result:', result);
      
      if (result.status === 'success') {
        // Step 4: Save to database and update cache (100% progress)
        setUploadProgress(100);
        const logoUrl = result.file_url;
        
        // Update user data with new logo URL
        setUserData(prev => ({
          ...prev,
          logoUrl: logoUrl
        }));
        
        // Logo is already saved in database during upload, no need to save again
        
        // Update cache
        const cacheKey = `logo_${currentUser.uid}`;
        localStorage.setItem(cacheKey, logoUrl);
        localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
        
        // Clean up modal
        setShowLogoModal(false);
        setLogoFile(null);
        setLogoPreview(null);
        
        window.alert('Logo uploaded successfully!');
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError(`Upload failed: ${error.message}`);
    } finally {
      setUploadingLogo(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveLogo = async () => {
    if (!userData.logoUrl || !currentUser) return;
    
    try {
      // Delete from Catalyst and update database
      const response = await fetch('https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/remove-logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: currentUser.uid,
          logo_url: userData.logoUrl
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove logo');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        // Update user data
        setUserData(prev => ({
          ...prev,
          logoUrl: null
        }));
        
        // Clear cache
        const cacheKey = `logo_${currentUser.uid}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_timestamp`);
        
        window.alert('Logo removed successfully!');
      } else {
        throw new Error(result.message || 'Remove failed');
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      window.alert('Error removing logo. Please try again.');
    }
  };

  const closeLogoModal = () => {
    setShowLogoModal(false);
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');
    setUploadProgress(0);
  };

  return (
    <div className="profile-page">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.9);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }
          .status-badge.in-progress {
            background: linear-gradient(135deg, #f59e0b, #d97706);
            color: white;
            animation: pulse 2s infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }
          .exp-verified {
            color: #4ade80;
            font-weight: 600;
            margin-left: 10px;
            font-size: 14px;
          }
          .exp-fill.verified {
            background: linear-gradient(135deg, #4ade80, #22c55e);
            box-shadow: 0 0 10px rgba(74, 222, 128, 0.3);
          }
        `}
      </style>
      <Header />

      <main className="profile-main">
        <div className="profile-header">
          <div className="profile-hero">
            <div className="profile-avatar-section">
              <div className="profile-avatar" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                <img 
                  src={userData.logoUrl || defaultLogo} 
                  alt={profileData.name}
                  onError={(e) => {
                    e.target.src = defaultLogo;
                  }}
                />
                <div className="avatar-border"></div>
                <div className="avatar-upload-overlay">
                  <span className="upload-icon">📷</span>
                </div>
              </div>
            </div>

            <div className="profile-info">
              {/* Account Details Section */}
              <div className="account-details">
                <div className="account-row">
                  <span className="account-label">Account Number:</span>
                  <span className="account-value">{userData.accountNumber}</span>
                </div>
                <div className="account-row">
                  <span className="account-label">Account Name:</span>
                  <span className="account-value">{userData.accountName}</span>
                </div>
                <div className="account-row">
                  <span className="account-label">Job / Designation:</span>
                  <span className="account-value">{userData.jobTitle}</span>
                </div>
                <div className="account-row">
                  <span className="account-label">Company:</span>
                  <span className="account-value">{formData.company_name}</span>
                </div>
                <div className="account-row verify-row">
                  <span className="account-label">Account Status:</span>
                  <div className="status-container">
                    <span className={`status-badge ${
                      verificationStatus === 'Verified' ? 'verified' : 
                      verificationStatus === 'In Progress' ? 'in-progress' : 
                      'unverified'
                    }`}>
                      {verificationStatus === 'Verified' ? '✓ Verified' : 
                       verificationStatus === 'In Progress' ? '⏳ In Progress' : 
                       '⚠ Unverified'}
                    </span>
                    {verificationStatus !== 'Verified' && (
                      <button 
                        className="verify-action-btn" 
                        onClick={() => {
                          if (verificationStatus === 'In Progress') {
                            setShowVerificationWaitModal(true);
                          } else {
                            openVerificationModal();
                          }
                        }}
                      >
                        {verificationStatus === 'In Progress' ? 'View Progress' : 'Verify Account'}
                      </button>
                    )}
                  </div>
                </div>
              

              <div className="experience-bar">
                <div className="exp-info">
                  <span className="exp-current">{Math.round(progressPercentage)}%</span>
                  <span className="exp-next">Complete</span>
                  {verificationStatus === 'Verified' && (
                    <span className="exp-verified">✓ Verified</span>
                  )}
                </div>
                <div className="exp-bar">
                  <div 
                    className={`exp-fill ${verificationStatus === 'Verified' ? 'verified' : ''}`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div className="profile-content">
          <div className="profile-tabs">
            <button
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => switchTab('profile')}
            >
              <span className="tab-icon"></span>
              Profile Information
            </button>
            <button
              className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => switchTab('company')}
            >
              <span className="tab-icon"></span>
              Company Information
            </button>
            <button
              className={`tab-btn ${activeTab === 'ubo' ? 'active' : ''}`}
              onClick={() => switchTab('ubo')}
            >
              <span className="tab-icon"></span>
              UBO Information
            </button>
            <button
              className={`tab-btn ${activeTab === 'authorized' ? 'active' : ''}`}
              onClick={() => switchTab('authorized')}
            >
              <span className="tab-icon"></span>
              Authorized Person
            </button>
            <button
              className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => switchTab('documents')}
            >
              <span className="tab-icon"></span>
              Uploaded Documents
            </button>
            <button
              className={`tab-btn ${activeTab === 'subscription' ? 'active' : ''}`}
              onClick={() => switchTab('subscription')}
            >
              <span className="tab-icon"></span>
              Subscription Plans
            </button>
            <button
              className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => switchTab('transactions')}
            >
              <span className="tab-icon"></span>
              Transaction History
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'profile' && (
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-heading">Profile Information</h3>
                  {verificationStatus !== 'Verified' && (
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditTab('profile')}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="field-grid">
                  <div className="field-row">
                    <span className="field-label">Name</span>
                    <span className="field-value">
                      {formData.firstName && formData.lastName 
                        ? `${formData.firstName} ${formData.lastName}` 
                        : formData.firstName || formData.lastName || 'Not provided'
                      }
                    </span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Job Title</span>
                    <span className="field-value">{formData.jobTitle || userData.jobTitle || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Primary Email</span>
                    <span className="field-value">{formData.primaryEmail || currentUser?.email || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Secondary Email</span>
                    <span className="field-value">{formData.secondaryEmail || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Primary Phone</span>
                    <span className="field-value">{formData.primaryPhone || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Secondary Phone</span>
                    <span className="field-value">{formData.secondaryPhone || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Street Address</span>
                    <span className="field-value">{formData.street || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">City</span>
                    <span className="field-value">{formData.city || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">State/Province</span>
                    <span className="field-value">{formData.state_province || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Country</span>
                    <span className="field-value">{formData.country || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Time Zone</span>
                    <span className="field-value">{formData.time_zone || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Zip Code</span>
                    <span className="field-value">{formData.zip_code || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'company' && (
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-heading">Company Information</h3>
                  {verificationStatus !== 'Verified' && (
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditTab('company')}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="field-grid">
                  <div className="field-row">
                    <span className="field-label">Company Name</span>
                    <span className="field-value">{formData.company_name || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Registration Number</span>
                    <span className="field-value">{formData.registration_number || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">License Number</span>
                    <span className="field-value">{formData.license_number || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Tax Registration</span>
                    <span className="field-value">{formData.tax_registration || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Website</span>
                    <span className="field-value">{formData.website || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Industry</span>
                    <span className="field-value">{formData.industry || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Size Type</span>
                    <span className="field-value">{formData.size_type || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Operations Type</span>
                    <span className="field-value">{formData.operations_type || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Employee Count</span>
                    <span className="field-value">{formData.employee_count || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Annual Revenue</span>
                    <span className="field-value">{formData.annual_revenue || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Company Email</span>
                    <span className="field-value">{formData.company_email || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Company Phone</span>
                    <span className="field-value">{formData.company_phone || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Secondary Company Phone</span>
                    <span className="field-value">{formData.secondary_company_phone || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Company Facebook</span>
                    <span className="field-value">{formData.company_facebook || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Company LinkedIn</span>
                    <span className="field-value">{formData.company_linkedin || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Business Address</span>
                    <span className="field-value">{formData.business_address || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ubo' && (
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-heading">UBO Information</h3>
                  {verificationStatus !== 'Verified' && (
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditTab('ubo')}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="field-grid">
                  <div className="field-row">
                    <span className="field-label">First Name</span>
                    <span className="field-value">{formData.ubo_first_name || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Last Name</span>
                    <span className="field-value">{formData.ubo_last_name || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Nationality</span>
                    <span className="field-value">{formData.ubo_nationality || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Birthdate</span>
                    <span className="field-value">{formData.ubo_birthdate || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Passport/National ID</span>
                    <span className="field-value">{formData.ubo_passport_id || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Ownership Share (%)</span>
                    <span className="field-value">{formData.ubo_ownership_share ? `${formData.ubo_ownership_share}%` : 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'authorized' && (
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-heading">Authorized Person</h3>
                  {verificationStatus !== 'Verified' && (
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditTab('authorized')}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="field-grid">
                  <div className="field-row">
                    <span className="field-label">First Name</span>
                    <span className="field-value">{formData.signatory_first_name || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Last Name</span>
                    <span className="field-value">{formData.signatory_last_name || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Nationality</span>
                    <span className="field-value">{formData.signatory_nationality || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Birthdate</span>
                    <span className="field-value">{formData.signatory_birthdate || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Passport/National ID</span>
                    <span className="field-value">{formData.signatory_passport_id || 'Not provided'}</span>
                  </div>
                  <div className="field-row">
                    <span className="field-label">Position</span>
                    <span className="field-value">{formData.signatory_position || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="section-card docs-card">
                <div className="section-header">
                  <h3 className="section-heading">Uploaded Documents</h3>
                  {verificationStatus !== 'Verified' && (
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditTab('documents')}
                    >
                      Edit
                    </button>
                  )}
                </div>
                
                {/* Company Registration Documents */}
                <div className="document-category">
                  <h4 className="category-title">Company Registration Documents</h4>
                  <div className="docs-table">
                    <div className="docs-header">
                      <span>Document Name</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Certificate of Incorporation / Business Registration Certificate</span>
                      <span className={`doc-status ${formData.documents?.certificate_incorporation ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.certificate_incorporation ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.certificate_incorporation ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('certificate_incorporation')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Business License (if applicable)</span>
                      <span className={`doc-status ${formData.documents?.business_license ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.business_license ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.business_license ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('business_license')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Tax Registration Certificate (TIN / VAT / GST No.)</span>
                      <span className={`doc-status ${formData.documents?.tax_registration_cert ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.tax_registration_cert ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.tax_registration_cert ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('tax_registration_cert')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Proof of Business Address</span>
                      <span className={`doc-status ${formData.documents?.business_address_proof ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.business_address_proof ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.business_address_proof ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('business_address_proof')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* UBO Information */}
                <div className="document-category">
                  <h4 className="category-title">UBO Information</h4>
                  <div className="docs-table">
                    <div className="docs-header">
                      <span>Document Name</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Shareholding Structure Chart</span>
                      <span className={`doc-status ${formData.documents?.shareholding_structure ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.shareholding_structure ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.shareholding_structure ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('shareholding_structure')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Authorized Signatory Proof */}
                <div className="document-category">
                  <h4 className="category-title">Authorized Signatory Proof</h4>
                  <div className="docs-table">
                    <div className="docs-header">
                      <span>Document Name</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">UBO's ID (passport/national ID) for each UBO holding ≥25% Ownership</span>
                      <span className={`doc-status ${formData.documents?.ubo_id_proof ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.ubo_id_proof ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.ubo_id_proof ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('ubo_id_proof')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Board Resolution OR Power of Attorney OR Company Letter</span>
                      <span className={`doc-status ${formData.documents?.board_resolution ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.board_resolution ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.board_resolution ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('board_resolution')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bank Account Proof */}
                <div className="document-category">
                  <h4 className="category-title">Bank Account Proof</h4>
                  <div className="docs-table">
                    <div className="docs-header">
                      <span>Document Name</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Recent Bank Statement (last 3 months, showing company name)</span>
                      <span className={`doc-status ${formData.documents?.bank_statement ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.bank_statement ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.bank_statement ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('bank_statement')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Canceled Cheque with company name</span>
                      <span className={`doc-status ${formData.documents?.canceled_cheque ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.canceled_cheque ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.canceled_cheque ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('canceled_cheque')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                    <div className="docs-row">
                      <span className="doc-name">Bank Certification Letter</span>
                      <span className={`doc-status ${formData.documents?.bank_certification ? 'uploaded' : 'pending'}`}>
                        {formData.documents?.bank_certification ? 'Uploaded' : 'Pending'}
                      </span>
                      <span className="doc-actions">
                        {formData.documents?.bank_certification ? (
                          <>
                            <button className="action-chip view" onClick={() => viewDocument('bank_certification')}>View</button>
                          </>
                        ) : (
                          <button className="action-chip upload">Upload</button>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div ref={subscriptionRef} id="subscription" className="subscription-tab">
                {/* Current Plan Display */}
                <div className="current-plan-section">
                  <h3 className="section-heading">Current Plan</h3>
                  <div className="current-plan-card">
                    <div className="plan-info">
                      <h4 className="plan-name">{profileData.currentPlan}</h4>
                      <p className="plan-description">Your active subscription plan</p>
                    </div>
                    <div className="plan-status">
                      <span className="status-active">Active</span>
                    </div>
                  </div>
                </div>

                {/* Subscription Plans */}
                <div className="plans-section">
                  <h3 className="section-heading">Available Plans</h3>
                  <div className="plans-grid">
                    {subscriptionPlans.map((plan, index) => (
                      <div
                        key={index}
                        className={`plan-card ${plan.current ? 'current' : ''}`}
                        onClick={() => handlePlanClick(plan)}
                      >
                        <div className="plan-header">
                          <h4 className="plan-title">{plan.name}</h4>
                          <div className="plan-price">{plan.price}</div>
                          {plan.current && <span className="current-badge">Current</span>}
                        </div>
                        <div className="plan-benefits">
                          <ul>
                            {plan.benefits.map((benefit, idx) => (
                              <li key={idx}>{benefit}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="plan-actions">
                          <button className="view-plan-btn">View Details</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add-ons */}
                <div className="addons-section">
                  <h3 className="section-heading">Add-ons</h3>
                  <div className="addons-grid">
                    {addOns.map((addon, index) => (
                      <div key={index} className="addon-card">
                        <div className="addon-icon">{addon.icon}</div>
                        <div className="addon-info">
                          <h4 className="addon-name">{addon.name}</h4>
                          <p className="addon-description">{addon.description}</p>
                        </div>
                        <div className="addon-price">{addon.price}</div>
                        <button 
                          className="addon-btn"
                          onClick={() => {
                            const type = addon.name.toLowerCase() === 'credit' ? 'credits' : 
                                         addon.name.toLowerCase() === 'talent role' ? 'talentRole' : 
                                         addon.name.toLowerCase() === 'shortlist pass' ? 'shortlistPass' : 'credits';
                            handleAddClick(type);
                          }}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
            <div className="section-card">
              <h3 className="section-heading">Transaction History</h3>

              <div className="docs-table tx6">
                <div className="docs-header">
                  <span>Date/Time</span>
                  <span>Type of Credit</span>
                  <span>Action</span>
                  <span>Balance</span>
                  <span>Total Cost</span>
                  <span>Note</span>
                </div>

                {(transactions || []).length === 0 && (
                  <div className="docs-row">
                    <span style={{ gridColumn: '1 / -1', opacity: 0.7 }}>
                      No transactions yet.
                    </span>
                  </div>
                )}

                {(transactions || []).map((t) => {
                  const usedCredits = (t.creditsDelta ?? 0) !== 0;
                  const type =
                    t.type ||
                    (usedCredits
                      ? 'Screening Credits'
                      : (t.passesDelta ?? 0) !== 0
                      ? 'Talent Role Pass'
                      : '—');

                  const delta = usedCredits ? t.creditsDelta : (t.passesDelta ?? 0);
                  const actionText = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : '0';
                  const balanceAfter = usedCredits
                    ? (t.creditsAfter ?? '—')
                    : (t.passesAfter ?? '—');

                  const dt = new Date(t.createdAt);
                  const dateText = isNaN(dt)
                    ? '—'
                    : dt.toLocaleString(undefined, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: 'numeric',
                        minute: '2-digit',
                      });

                  const totalCost = t.totalCost && t.totalCost > 0 ? `$${t.totalCost.toFixed(2)}` : '—';

                  return (
                    <div key={t.id} className="docs-row">
                      <span className="doc-name">{dateText}</span>
                      <span className="doc-name">{type}</span>
                      <span
                        className="doc-name"
                        style={{ color: delta < 0 ? '#c0392b' : delta > 0 ? '#27ae60' : 'inherit' }}
                      >
                        {actionText}
                      </span>
                      <span className="doc-name">{balanceAfter}</span>
                      <span className="doc-name" style={{ color: t.totalCost && t.totalCost > 0 ? '#27ae60' : 'inherit' }}>
                        {totalCost}
                      </span>
                      <span className="doc-name">{t.note || '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Plan Details Modal */}
      {showPlanModal && selectedPlan && (
        <div className="plan-modal-overlay" onClick={closePlanModal}>
          <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{selectedPlan.name} Plan</h3>
              <button className="close-btn" onClick={closePlanModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="plan-details">
                <div className="plan-price-large">{selectedPlan.price}</div>
                <div className="plan-benefits-detailed">
                  <h4>Benefits:</h4>
                  <ul>
                    {selectedPlan.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                  </ul>
                </div>
                <div className="plan-features">
                  <h4>Features:</h4>
                  <ul>
                    {selectedPlan.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="modal-actions">
                {!selectedPlan.current && (
                  <button
                    className="upgrade-btn"
                    onClick={() => handleUpgrade(selectedPlan.name)}
                  >
                    {selectedPlan.price === 'Contact Sales' ? 'Contact Sales' : `Upgrade to ${selectedPlan.name}`}
                  </button>
                )}
                <button className="cancel-btn" onClick={closePlanModal}>
                  {selectedPlan.current ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gamified Business Verification Modal - 5-Step Form Flow */}
      {showVerificationModal && (
        <div className="gamified-verification-modal">
          <div className="gvm-modal">
            {/* Floating particles for gamified vibe */}
            <div className="gvm-particles">
              {Array.from({ length: 20 }).map((_, i) => (
                <span key={i} style={{ '--i': i }} />
              ))}
            </div>

            <div className="gvm-header">
              <h3 className="gvm-title">Account Verification Quest</h3>
              <p className="gvm-subtitle">
                Complete all steps to unlock your verified account status
              </p>
              <button 
                className="gvm-close-button"
                onClick={() => setShowVerificationModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="gvm-body">
              {/* Gamified Progress Quest Bar */}
              <div className="gvm-quest-progress">
                <div className="gvm-quest-header">
                  <h4>Verification Quest Progress</h4>
                  <div className="gvm-xp-bar">
                    <div className="gvm-xp-fill" style={{ 
                      width: `${verificationStatus === 'Verified' ? 100 : (totalExperience / totalQuestXP) * 100}%` 
                    }}></div>
                    <span className="gvm-xp-text">
                      {verificationStatus === 'Verified' ? '100% Complete' : `${Math.round((totalExperience / totalQuestXP) * 100)}% Complete`}
                    </span>
                </div>
                </div>
                
                <div className="gvm-quest-steps">
                  <div className={`gvm-quest-step ${currentStep >= 0 ? 'active' : ''} ${stepCompletion[0] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[0] ? '✓' : '0'}
                </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">Agreement</span>
                      <span className="gvm-step-desc">Accept Terms</span>
                </div>
                    <div className="gvm-step-xp">+20 XP</div>
                </div>
                  
                  <div className={`gvm-quest-step ${currentStep >= 1 ? 'active' : ''} ${stepCompletion[1] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[1] ? '✓' : '1'}
                </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">Profile</span>
                      <span className="gvm-step-desc">Personal Info</span>
                    </div>
                    <div className="gvm-step-xp">+40 XP</div>
              </div>

                  <div className={`gvm-quest-step ${currentStep >= 2 ? 'active' : ''} ${stepCompletion[2] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[2] ? '✓' : '2'}
                    </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">Company</span>
                      <span className="gvm-step-desc">Business Details</span>
                    </div>
                    <div className="gvm-step-xp">+60 XP</div>
                  </div>
                  
                  <div className={`gvm-quest-step ${currentStep >= 3 ? 'active' : ''} ${stepCompletion[3] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[3] ? '✓' : '3'}
                    </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">UBO</span>
                      <span className="gvm-step-desc">Ultimate Beneficiary</span>
                    </div>
                    <div className="gvm-step-xp">+80 XP</div>
                  </div>
                  
                  <div className={`gvm-quest-step ${currentStep >= 4 ? 'active' : ''} ${stepCompletion[4] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[4] ? '✓' : '4'}
                    </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">Signatory</span>
                      <span className="gvm-step-desc">Authorized Person</span>
                    </div>
                    <div className="gvm-step-xp">+100 XP</div>
                  </div>
                  
                  <div className={`gvm-quest-step ${currentStep >= 5 ? 'active' : ''} ${stepCompletion[5] ? 'completed' : ''}`}>
                    <div className="gvm-step-icon">
                      {stepCompletion[5] ? '✓' : '5'}
                    </div>
                    <div className="gvm-step-info">
                      <span className="gvm-step-title">Documents</span>
                      <span className="gvm-step-desc">Upload Files</span>
                    </div>
                    <div className="gvm-step-xp">+120 XP</div>
                  </div>
                </div>
              </div>

              {/* Gamified Form Steps - Only render current step */}
              <div className="gvm-form-steps">
                {/* Step 0: Agreement/Confirmation */}
                {currentStep === 0 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>Quest Agreement</h4>
                      <div className="gvm-step-reward">Reward: +20 XP</div>
                    </div>
                    <div className="gvm-agreement-section">
                      <div className="gvm-agreement-text">
                        <p>By proceeding with this verification quest, you pledge that:</p>
                        <ul>
                          <li>All information provided is accurate and truthful</li>
                          <li>You are authorized to provide this information on behalf of the company</li>
                          <li>You understand that false information may result in account suspension</li>
                          <li>You consent to verification of the provided information</li>
                        </ul>
                        <p><strong>Please ensure all information is legitimate and real before proceeding.</strong></p>
                      </div>
                      <div className="gvm-agreement-checkbox">
                        <label className="gvm-checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={formData.agreementAccepted || false}
                            onChange={(e) => handleFormChange('agreementAccepted', e.target.checked)}
                            className="gvm-checkbox"
                          />
                          <span className="gvm-checkbox-text">I accept the quest terms and confirm all information is legitimate</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Profile Information */}
                {currentStep === 1 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>Profile Information Quest</h4>
                      <div className="gvm-step-reward">Reward: +40 XP</div>
                    </div>
                    <div className="gvm-form-grid">
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">First Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter first name" 
                          value={formData.firstName}
                          onChange={(e) => handleFormChange('firstName', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Last Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter last name" 
                          value={formData.lastName}
                          onChange={(e) => handleFormChange('lastName', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Job Title / Designation *</label>
                        <input 
                          type="text" 
                          placeholder="Enter job title" 
                          value={formData.jobTitle}
                          onChange={(e) => handleFormChange('jobTitle', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Primary Email *</label>
                        <input 
                          type="email" 
                          placeholder="Enter primary email" 
                          value={formData.primaryEmail}
                          onChange={(e) => handleFormChange('primaryEmail', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Secondary Email</label>
                        <input 
                          type="email" 
                          placeholder="Enter secondary email" 
                          value={formData.secondaryEmail}
                          onChange={(e) => handleFormChange('secondaryEmail', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Primary Phone Number *</label>
                        <input 
                          type="tel" 
                          placeholder="Enter primary phone number" 
                          value={formData.primaryPhone}
                          onChange={(e) => handleFormChange('primaryPhone', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Secondary Phone Number</label>
                        <input 
                          type="tel" 
                          placeholder="Enter secondary phone number" 
                          value={formData.secondaryPhone}
                          onChange={(e) => handleFormChange('secondaryPhone', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Facebook</label>
                        <input 
                          type="url" 
                          placeholder="Enter Facebook profile URL" 
                          value={formData.facebook}
                          onChange={(e) => handleFormChange('facebook', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">LinkedIn Profile</label>
                        <input 
                          type="url" 
                          placeholder="Enter LinkedIn profile URL" 
                          value={formData.linkedin_profile}
                          onChange={(e) => handleFormChange('linkedin_profile', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Street *</label>
                        <input 
                          type="text" 
                          placeholder="Enter street address" 
                          value={formData.street}
                          onChange={(e) => handleFormChange('street', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">City *</label>
                        <input 
                          type="text" 
                          placeholder="Enter city" 
                          value={formData.city}
                          onChange={(e) => handleFormChange('city', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">State/Province *</label>
                        <input 
                          type="text" 
                          placeholder="Enter state/province" 
                          value={formData.state_province}
                          onChange={(e) => handleFormChange('state_province', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Country *</label>
                        <input 
                          type="text" 
                          placeholder="Enter country" 
                          value={formData.country}
                          onChange={(e) => handleFormChange('country', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Time Zone *</label>
                        <select 
                          value={formData.time_zone}
                          onChange={(e) => handleFormChange('time_zone', e.target.value)}
                          className="gvm-select"
                          required
                        >
                          <option value="">Select Time Zone</option>
                          <option value="UTC-12">UTC-12:00 (Baker Island, Howland Island)</option>
                          <option value="UTC-11">UTC-11:00 (American Samoa, Niue)</option>
                          <option value="UTC-10">UTC-10:00 (Hawaii, Tahiti)</option>
                          <option value="UTC-9">UTC-09:00 (Alaska, Gambier Islands)</option>
                          <option value="UTC-8">UTC-08:00 (Pacific Time, Los Angeles)</option>
                          <option value="UTC-7">UTC-07:00 (Mountain Time, Denver)</option>
                          <option value="UTC-6">UTC-06:00 (Central Time, Chicago)</option>
                          <option value="UTC-5">UTC-05:00 (Eastern Time, New York)</option>
                          <option value="UTC-4">UTC-04:00 (Atlantic Time, Halifax)</option>
                          <option value="UTC-3">UTC-03:00 (Brasília, Buenos Aires)</option>
                          <option value="UTC-2">UTC-02:00 (Fernando de Noronha)</option>
                          <option value="UTC-1">UTC-01:00 (Azores, Cape Verde)</option>
                          <option value="UTC+0">UTC+00:00 (London, Lisbon, Accra)</option>
                          <option value="UTC+1">UTC+01:00 (Paris, Berlin, Rome)</option>
                          <option value="UTC+2">UTC+02:00 (Cairo, Helsinki, Kyiv)</option>
                          <option value="UTC+3">UTC+03:00 (Moscow, Istanbul, Riyadh)</option>
                          <option value="UTC+4">UTC+04:00 (Dubai, Baku, Tbilisi)</option>
                          <option value="UTC+5">UTC+05:00 (Tashkent, Karachi, Mumbai)</option>
                          <option value="UTC+5:30">UTC+05:30 (New Delhi, Colombo)</option>
                          <option value="UTC+6">UTC+06:00 (Dhaka, Almaty, Omsk)</option>
                          <option value="UTC+7">UTC+07:00 (Bangkok, Jakarta, Ho Chi Minh)</option>
                          <option value="UTC+8">UTC+08:00 (Beijing, Hong Kong, Singapore)</option>
                          <option value="UTC+9">UTC+09:00 (Tokyo, Seoul, Pyongyang)</option>
                          <option value="UTC+10">UTC+10:00 (Sydney, Melbourne, Brisbane)</option>
                          <option value="UTC+11">UTC+11:00 (Vladivostok, Solomon Islands)</option>
                          <option value="UTC+12">UTC+12:00 (Auckland, Fiji, Kamchatka)</option>
                        </select>
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Zip Code *</label>
                        <input 
                          type="text" 
                          placeholder="Enter zip code" 
                          value={formData.zip_code}
                          onChange={(e) => handleFormChange('zip_code', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Company Information */}
                {currentStep === 2 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>Company Information Quest</h4>
                      <div className="gvm-step-reward">Reward: +60 XP</div>
                    </div>
                    <div className="gvm-form-grid">
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Company Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter company name" 
                          value={formData.company_name}
                          onChange={(e) => handleFormChange('company_name', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Registration Number *</label>
                        <input 
                          type="text" 
                          placeholder="Enter registration number" 
                          value={formData.registration_number}
                          onChange={(e) => handleFormChange('registration_number', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">License Number</label>
                        <input 
                          type="text" 
                          placeholder="Enter license number" 
                          value={formData.license_number}
                          onChange={(e) => handleFormChange('license_number', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Tax Registration *</label>
                        <input 
                          type="text" 
                          placeholder="Enter tax registration" 
                          value={formData.tax_registration}
                          onChange={(e) => handleFormChange('tax_registration', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Website *</label>
                        <input 
                          type="url" 
                          placeholder="Enter website URL" 
                          value={formData.website}
                          onChange={(e) => handleFormChange('website', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Industry *</label>
                        <input 
                          type="text" 
                          placeholder="Enter industry" 
                          value={formData.industry}
                          onChange={(e) => handleFormChange('industry', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Size Type *</label>
                        <select 
                          value={formData.size_type}
                          onChange={(e) => handleFormChange('size_type', e.target.value)}
                          className="gvm-select"
                          required
                        >
                          <option value="">Select Size Type</option>
                          <option value="Small">Small (1-50 employees)</option>
                          <option value="Medium">Medium (51-200 employees)</option>
                          <option value="Large">Large (201+ employees)</option>
                        </select>
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Operations Type *</label>
                        <select 
                          value={formData.operations_type}
                          onChange={(e) => handleFormChange('operations_type', e.target.value)}
                          className="gvm-select"
                          required
                        >
                          <option value="">Select Operations Type</option>
                          <option value="Local">Local</option>
                          <option value="National">National</option>
                          <option value="International">International</option>
                        </select>
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Employee Count *</label>
                        <input 
                          type="number" 
                          placeholder="Enter employee count" 
                          value={formData.employee_count}
                          onChange={(e) => handleFormChange('employee_count', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Annual Revenue</label>
                        <input 
                          type="text" 
                          placeholder="Enter annual revenue" 
                          value={formData.annual_revenue}
                          onChange={(e) => handleFormChange('annual_revenue', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Business Address *</label>
                        <textarea 
                          placeholder="Enter business address" 
                          value={formData.business_address}
                          onChange={(e) => handleFormChange('business_address', e.target.value)}
                          className="gvm-textarea"
                          required
                          rows="3"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Company Email *</label>
                        <input 
                          type="email" 
                          placeholder="Enter company email" 
                          value={formData.company_email}
                          onChange={(e) => handleFormChange('company_email', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Company Phone *</label>
                        <input 
                          type="tel" 
                          placeholder="Enter company phone" 
                          value={formData.company_phone}
                          onChange={(e) => handleFormChange('company_phone', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Secondary Company Phone</label>
                        <input 
                          type="tel" 
                          placeholder="Enter secondary company phone" 
                          value={formData.secondary_company_phone}
                          onChange={(e) => handleFormChange('secondary_company_phone', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Company Facebook</label>
                        <input 
                          type="url" 
                          placeholder="Enter company Facebook URL" 
                          value={formData.company_facebook}
                          onChange={(e) => handleFormChange('company_facebook', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Company LinkedIn</label>
                        <input 
                          type="url" 
                          placeholder="Enter company LinkedIn URL" 
                          value={formData.company_linkedin}
                          onChange={(e) => handleFormChange('company_linkedin', e.target.value)}
                          className="gvm-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: UBO Information */}
                {currentStep === 3 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>UBO Information Quest</h4>
                      <div className="gvm-step-reward">Reward: +80 XP</div>
                    </div>
                    <div className="gvm-form-grid">
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">First Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter UBO first name" 
                          value={formData.ubo_first_name}
                          onChange={(e) => handleFormChange('ubo_first_name', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Last Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter UBO last name" 
                          value={formData.ubo_last_name}
                          onChange={(e) => handleFormChange('ubo_last_name', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Nationality *</label>
                        <input 
                          type="text" 
                          placeholder="Enter nationality" 
                          value={formData.ubo_nationality}
                          onChange={(e) => handleFormChange('ubo_nationality', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Birthdate *</label>
                        <input 
                          type="date" 
                          value={formData.ubo_birthdate}
                          onChange={(e) => handleFormChange('ubo_birthdate', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Passport/National ID *</label>
                        <input 
                          type="text" 
                          placeholder="Enter passport or national ID" 
                          value={formData.ubo_passport_id}
                          onChange={(e) => handleFormChange('ubo_passport_id', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Ownership Share (%) *</label>
                        <input 
                          type="number" 
                          placeholder="Enter ownership percentage" 
                          value={formData.ubo_ownership_share}
                          onChange={(e) => handleFormChange('ubo_ownership_share', e.target.value)}
                          className="gvm-input"
                          min="0"
                          max="100"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Authorized Signatory */}
                {currentStep === 4 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>Authorized Signatory Quest</h4>
                      <div className="gvm-step-reward">Reward: +100 XP</div>
                    </div>
                    <div className="gvm-form-grid">
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">First Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter signatory first name" 
                          value={formData.signatory_first_name}
                          onChange={(e) => handleFormChange('signatory_first_name', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Last Name *</label>
                        <input 
                          type="text" 
                          placeholder="Enter signatory last name" 
                          value={formData.signatory_last_name}
                          onChange={(e) => handleFormChange('signatory_last_name', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Nationality *</label>
                        <input 
                          type="text" 
                          placeholder="Enter nationality" 
                          value={formData.signatory_nationality}
                          onChange={(e) => handleFormChange('signatory_nationality', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Birthdate *</label>
                        <input 
                          type="date" 
                          value={formData.signatory_birthdate}
                          onChange={(e) => handleFormChange('signatory_birthdate', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Passport/National ID *</label>
                        <input 
                          type="text" 
                          placeholder="Enter passport or national ID" 
                          value={formData.signatory_passport_id}
                          onChange={(e) => handleFormChange('signatory_passport_id', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                      <div className="gvm-form-field">
                        <label className="gvm-field-label">Position *</label>
                        <input 
                          type="text" 
                          placeholder="Enter position in company" 
                          value={formData.signatory_position}
                          onChange={(e) => handleFormChange('signatory_position', e.target.value)}
                          className="gvm-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Document Upload */}
                {currentStep === 5 && (
                  <div className="gvm-form-step active">
                    <div className="gvm-step-header">
                      <h4>Document Upload Quest</h4>
                      <div className="gvm-step-reward">Reward: +120 XP</div>
                    </div>
                    <div className="gvm-documents-section">
                      <div className="gvm-quest-instructions">
                        <p>Complete your document collection quest! Upload files to enhance your verification (all documents are optional for this demo).</p>
                      </div>
                      
                      {/* Company Registration Documents */}
                      <div className="gvm-document-category">
                        <div className="gvm-category-header">
                        <h5>Company Registration Documents</h5>
                          <div className="gvm-category-progress">
                            <span className="gvm-progress-text">0/4 uploaded</span>
                          </div>
                        </div>
                        
                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.certificate_incorporation ? '✓' : '📄'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Certificate of Incorporation / Business Registration Certificate</label>
                              <div className="gvm-document-status">
                                {formData.documents?.certificate_incorporation ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="certificate_incorporation"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'certificate_incorporation')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="certificate_incorporation" className="gvm-upload-btn">
                              {formData.documents?.certificate_incorporation ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.certificate_incorporation && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.certificate_incorporation.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('certificate_incorporation')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.business_license ? '✓' : '📄'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Business License (if applicable)</label>
                              <div className="gvm-document-status">
                                {formData.documents?.business_license ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="business_license"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'business_license')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="business_license" className="gvm-upload-btn">
                              {formData.documents?.business_license ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.business_license && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.business_license.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('business_license')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.tax_registration_cert ? '✓' : '📄'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Tax Registration Certificate (TIN / VAT / GST No.)</label>
                              <div className="gvm-document-status">
                                {formData.documents?.tax_registration_cert ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="tax_registration_cert"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'tax_registration_cert')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="tax_registration_cert" className="gvm-upload-btn">
                              {formData.documents?.tax_registration_cert ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.tax_registration_cert && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.tax_registration_cert.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('tax_registration_cert')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.business_address_proof ? '✓' : '📄'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Proof of Business Address</label>
                              <div className="gvm-document-status">
                                {formData.documents?.business_address_proof ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="business_address_proof"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'business_address_proof')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="business_address_proof" className="gvm-upload-btn">
                              {formData.documents?.business_address_proof ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.business_address_proof && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.business_address_proof.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('business_address_proof')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* UBO Information */}
                      <div className="gvm-document-category">
                        <div className="gvm-category-header">
                        <h5>UBO Information</h5>
                          <div className="gvm-category-progress">
                            <span className="gvm-progress-text">0/1 uploaded</span>
                          </div>
                        </div>
                        
                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.shareholding_structure ? '✓' : '📊'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Shareholding Structure Chart</label>
                              <div className="gvm-document-status">
                                {formData.documents?.shareholding_structure ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="shareholding_structure"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'shareholding_structure')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="shareholding_structure" className="gvm-upload-btn">
                              {formData.documents?.shareholding_structure ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.shareholding_structure && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.shareholding_structure.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('shareholding_structure')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Authorized Signatory Proof */}
                      <div className="gvm-document-category">
                        <div className="gvm-category-header">
                        <h5>Authorized Signatory Proof</h5>
                          <div className="gvm-category-progress">
                            <span className="gvm-progress-text">0/2 uploaded</span>
                          </div>
                        </div>
                        
                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.ubo_id_proof ? '✓' : '🆔'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">UBO's ID (passport/national ID) for each UBO holding ≥25% Ownership</label>
                              <div className="gvm-document-status">
                                {formData.documents?.ubo_id_proof ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="ubo_id_proof"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'ubo_id_proof')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="ubo_id_proof" className="gvm-upload-btn">
                              {formData.documents?.ubo_id_proof ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.ubo_id_proof && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.ubo_id_proof.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('ubo_id_proof')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.board_resolution ? '✓' : '📋'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Board Resolution OR Power of Attorney OR Company Letter</label>
                              <div className="gvm-document-status">
                                {formData.documents?.board_resolution ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="board_resolution"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'board_resolution')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="board_resolution" className="gvm-upload-btn">
                              {formData.documents?.board_resolution ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.board_resolution && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.board_resolution.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('board_resolution')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bank Account Proof */}
                      <div className="gvm-document-category">
                        <div className="gvm-category-header">
                        <h5>Bank Account Proof</h5>
                          <div className="gvm-category-progress">
                            <span className="gvm-progress-text">0/3 uploaded</span>
                          </div>
                        </div>
                        
                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.bank_statement ? '✓' : '🏦'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Recent Bank Statement (last 3 months, showing company name)</label>
                              <div className="gvm-document-status">
                                {formData.documents?.bank_statement ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="bank_statement"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'bank_statement')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="bank_statement" className="gvm-upload-btn">
                              {formData.documents?.bank_statement ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.bank_statement && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.bank_statement.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('bank_statement')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.canceled_cheque ? '✓' : '💳'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Canceled Cheque with company name</label>
                              <div className="gvm-document-status">
                                {formData.documents?.canceled_cheque ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="canceled_cheque"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'canceled_cheque')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="canceled_cheque" className="gvm-upload-btn">
                              {formData.documents?.canceled_cheque ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.canceled_cheque && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.canceled_cheque.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('canceled_cheque')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="gvm-document-item">
                          <div className="gvm-document-info">
                            <div className="gvm-document-icon">
                              {formData.documents?.bank_certification ? '✓' : '📜'}
                            </div>
                            <div className="gvm-document-details">
                              <label className="gvm-document-label">Bank Certification Letter</label>
                              <div className="gvm-document-status">
                                {formData.documents?.bank_certification ? (
                                  <span className="gvm-status-completed">Quest Item Completed!</span>
                                ) : (
                                  <span className="gvm-status-optional">Optional Quest Item</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="gvm-upload-area">
                            <input
                              type="file"
                              id="bank_certification"
                              accept=".pdf"
                              onChange={(e) => handleFileUpload(e, 'bank_certification')}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="bank_certification" className="gvm-upload-btn">
                              {formData.documents?.bank_certification ? 'Replace Quest Item' : 'Upload Quest Item'}
                            </label>
                            {formData.documents?.bank_certification && (
                              <div className="gvm-uploaded-file">
                                <span className="gvm-file-name">{formData.documents.bank_certification.name}</span>
                                <button className="gvm-remove-btn" onClick={() => removeDocument('bank_certification')}>Remove</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Gamified Action Buttons */}
                <div className="gvm-form-actions">
                  {/* Back button - only show if not on first step */}
                  {currentStep > 0 && (
                    <button className="gvm-btn gvm-btn-secondary" onClick={prevStep}>
                      Previous Quest
                    </button>
                  )}
                  
                  {/* Contract HQ button - always visible */}
                  <button className="gvm-btn gvm-btn-outline">
                    Contact HQ
                  </button>
                  
                  {/* Save and Continue Later - always visible */}
                  <button className="gvm-btn gvm-btn-outline" onClick={saveAndContinueLater}>
                    Save Progress
                  </button>
                  
                  {/* Next/Continue button - different text based on step */}
                  {currentStep === 0 && (
                    <button 
                      className="gvm-btn gvm-btn-primary" 
                      onClick={nextStep}
                      disabled={!formData.agreementAccepted}
                    >
                      Accept Quest
                    </button>
                  )}
                  
                  {currentStep > 0 && currentStep < 5 && (
                    <button className="gvm-btn gvm-btn-primary" onClick={saveAndContinue}>
                      Complete Step
                    </button>
                  )}
                  
                  {currentStep === 5 && (
                    <button className="gvm-btn gvm-btn-success" onClick={handleVerification}>
                      Complete Quest
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Upload Modal */}
      {showLogoModal && (
        <div className="logo-upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Upload Logo</h3>
              <button className="close-button" onClick={closeLogoModal}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="logo-upload-section">
                <div className="current-logo">
                  <h4>Current Logo:</h4>
                  <div className="logo-preview-container">
                    <img 
                      src={userData.logoUrl || defaultLogo} 
                      alt="Current logo"
                      className="current-logo-img"
                    />
                    {userData.logoUrl && (
                      <button 
                        className="remove-logo-btn"
                        onClick={handleRemoveLogo}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="upload-new-logo">
                  <h4>Upload New Logo:</h4>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="logo-upload" className="file-upload-label">
                      <div className="upload-icon-large">📷</div>
                      <span>Choose Logo Image</span>
                      <small>Max 5MB, JPG/PNG/GIF/WebP</small>
                    </label>
                  </div>
                  
                  {logoPreview && (
                    <div className="new-logo-preview">
                      <h5>Preview:</h5>
                      <img src={logoPreview} alt="Preview" className="preview-img" />
                    </div>
                  )}
                  
                  {/* Error Display */}
                  {logoError && (
                    <div className="logo-error" style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                      {logoError}
                    </div>
                  )}
                  
                  {/* Progress Indicator */}
                  {uploadingLogo && (
                    <div className="upload-progress" style={{ marginTop: '15px' }}>
                      <div className="progress-bar" style={{ 
                        width: '100%', 
                        height: '8px', 
                        backgroundColor: '#f0f0f0', 
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: `${uploadProgress}%`, 
                            height: '100%', 
                            backgroundColor: '#007bff', 
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <div className="progress-text" style={{ 
                        marginTop: '5px', 
                        fontSize: '12px', 
                        color: '#666',
                        textAlign: 'center'
                      }}>
                        {uploadProgress < 25 && 'Compressing image...'}
                        {uploadProgress >= 25 && uploadProgress < 50 && 'Preparing upload...'}
                        {uploadProgress >= 50 && uploadProgress < 75 && 'Uploading to server...'}
                        {uploadProgress >= 75 && uploadProgress < 100 && 'Saving to database...'}
                        {uploadProgress === 100 && 'Complete!'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeLogoModal} disabled={uploadingLogo}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleLogoUpload}
                disabled={!logoFile || uploadingLogo}
              >
                {uploadingLogo ? `Uploading... ${uploadProgress}%` : 'Upload Logo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showDocumentModal && currentDocument && (
        <div className="document-viewer-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>View Document</h3>
              <button className="close-button" onClick={closeDocumentModal}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="document-info">
                <h4>{currentDocument.name}</h4>
                <p>Document Type: {currentDocument.type.replace(/_/g, ' ')}</p>
                <p>Uploaded: {new Date(currentDocument.uploadTime).toLocaleString()}</p>
              </div>
              
              <div className="document-viewer">
                {currentDocument.url.startsWith('data:image/') ? (
                  <img 
                    src={currentDocument.url} 
                    alt={currentDocument.name}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '500px', 
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                ) : (
                  <div className="document-preview">
                    <iframe
                      src={`${currentDocument.url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                      style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #ddd',
                        borderRadius: '8px'
                      }}
                      title={currentDocument.name}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeDocumentModal}>
                Close
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = currentDocument.url;
                  link.download = currentDocument.name;
                  link.click();
                }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={closePurchaseModal}
        onConfirm={handlePurchaseConfirm}
        type={purchaseType}
        currentBalance={purchaseType === 'credits' ? screeningCredits : purchaseType === 'talentRole' ? talentRolePass : shortlistPass}
        price={purchaseType === 'credits' ? 99.99 : purchaseType === 'talentRole' ? 29.99 : 9.99}
      />

      {/* Verification Wait Modal */}
      {showVerificationWaitModal && (
        <div 
          className="verification-wait-overlay" 
          onClick={() => setShowVerificationWaitModal(false)} // Allow closing by clicking overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.4s ease-out'
          }}
        >
          <div 
            className="verification-wait-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              animation: 'slideInUp 0.4s ease-out'
            }}
          >
            <div style={{ marginBottom: '30px', position: 'relative' }}>
              {/* Close button */}
              <button
                onClick={() => setShowVerificationWaitModal(false)}
                style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#b8c5d6',
                  fontSize: '20px',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#b8c5d6';
                }}
              >
                ×
              </button>
              
              <div style={{ 
                fontSize: '64px', 
                marginBottom: '20px',
                animation: 'bounce 2s infinite'
              }}>
                ⏳
              </div>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                margin: '0 0 15px 0',
                textShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Verification In Progress
              </h2>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '18px', 
                lineHeight: '1.6',
                margin: '0 0 20px 0'
              }}>
                Your account verification is being processed. Please wait while we review your information.
              </p>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '30px',
              marginBottom: '30px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ 
                fontSize: '48px', 
                marginBottom: '20px',
                color: '#4ade80'
              }}>
                {verificationTimer}
              </div>
              <p style={{ 
                color: '#ffffff', 
                fontSize: '18px', 
                fontWeight: '600', 
                margin: '0 0 10px 0' 
              }}>
                {verificationTimer === 1 ? 'Second' : 'Seconds'} remaining
              </p>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '14px', 
                margin: '0' 
              }}>
                (3 hours in production)
              </p>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '14px', 
                margin: '0',
                fontStyle: 'italic'
              }}>
                🔒 Your account is secure and your information is encrypted
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default ProfilePage;
